import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Group, List, Loader, Pagination, ScrollArea, SegmentedControl, Stack, Table, Text, Textarea, Title, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconDownload, IconFileText, IconRefresh } from '@tabler/icons-react';
import * as Enums from '@/utils/enums';
import ImportDraftService, { errorMessage, isApiError } from '@/services/import-draft/import-draft-service';
import { MixedContactsNotice, SavedRecipeNotice } from './RecipeNotices';
import { ImportDraft, ImportDraftPreview } from '@/services/import-draft/import-draft-model';
import { formatTokens, heldBackCount, heldBackIssues, isExtracted, isSavedRecipe, softIssues, pageRangeLabel, queueableCount, rowsForPreviewPage, softIssueCount } from '@/services/import-draft/import-draft-helpers';

interface Props {
  draft: ImportDraft;
  onBack: () => void;
  onContinue: () => void;
  onReread?: (instruction: string) => void;
  onRetry?: (instruction: string) => void;
  onRedetect?: () => void;
  busy?: boolean;
}

const PAGE_SIZE = 25;

export default function DataStep({ draft, onBack, onContinue, onReread, onRetry, onRedetect, busy }: Props) {
  const extracted = isExtracted(draft);
  const usedSavedRules = extracted && draft.RecipeSource === Enums.ImportRecipeSource.Saved;
  const [instruction, setInstruction] = useState(draft.UserInstruction || '');
  const [preview, setPreview] = useState<ImportDraftPreview | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const requestKey = `${draft.ID}:${pageIndex}:${issuesOnly ? 1 : 0}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ImportDraftService.preview(draft.ID, pageIndex, PAGE_SIZE, issuesOnly).then(result => {
      if (cancelled) return;
      if (isApiError(result)) {
        setError(errorMessage(result, 'The preview could not be loaded.'));
        setPreview(null);
      } else {
        const next = result as ImportDraftPreview;
        const rows = rowsForPreviewPage(next.Page, pageIndex, PAGE_SIZE);
        setError(null);
        setPreview({ ...next, Page: { ...next.Page, PageIndex: pageIndex, PageSize: PAGE_SIZE, Rows: rows } });
        setTotalRows(next.Page?.TotalRows || 0);
      }
      setLoadedKey(requestKey);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [draft.ID, draft.ModifiedDate, pageIndex, issuesOnly, requestKey]);

  const pageReady = loadedKey === requestKey ? preview : null;
  const summary = pageReady?.Summary || draft.Summary;
  const page = pageReady?.Page;
  const columns = pageReady?.Columns || draft.Recipe?.Fields.map(f => f.Target) || [];
  const knownTotal = page?.TotalRows || totalRows;
  const pageCount = knownTotal > 0 ? Math.max(1, Math.ceil(knownTotal / PAGE_SIZE)) : 1;
  const range = pageRangeLabel(pageIndex, PAGE_SIZE, knownTotal, page?.Rows.length || 0);

  return (
    <Stack gap="md" data-testid="data-step">
      <div>
        <Title order={4}>Review the records</Title>
        <Text size="sm" c="dimmed">Records that would be rejected are held back and are not queued. Other warnings are still imported and can be corrected afterwards.</Text>
      </div>

      {extracted && (
        <Alert
          color={usedSavedRules ? 'green' : 'blue'}
          icon={<IconFileText size={16} />}
          title={usedSavedRules ? 'Processed with saved rules' : 'Processed as a report'}
          data-testid="extraction-summary"
        >
          {draft.ModeReason && <Text size="sm">{draft.ModeReason}</Text>}
          {(draft.Recipe?.Warnings || []).map((w, i) => <Text size="sm" key={i}>• {w}</Text>)}
          <Text size="sm" mt={4}>
            {usedSavedRules
              ? 'This report matched rules saved from an earlier import, so no AI processing was needed. The Source column shows the line where each record starts.'
              : `Each value was checked against the file, and values that could not be found in it were removed. The Source column shows the line where each record starts.${formatTokens(draft) ? ` ${formatTokens(draft)}.` : ''}`}
          </Text>
          {usedSavedRules && onRedetect && (
            <Button mt="xs" size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={onRedetect} loading={busy} data-testid="redetect">
              Re-detect
            </Button>
          )}
        </Alert>
      )}

      {!extracted && isSavedRecipe(draft) && <SavedRecipeNotice draft={draft} busy={busy} onRedetect={onRedetect} />}
      <MixedContactsNotice draft={draft} />

      {summary && heldBackCount(summary) > 0 && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title={`${heldBackCount(summary).toLocaleString()} records have errors and will not be imported`} data-testid="held-back-errors">
          <List size="sm" spacing={2}>
            {heldBackIssues(summary).slice(0, 8).map(issue => (
              <List.Item key={`${issue.Target}-${issue.Message}`}>{issue.Count.toLocaleString()} records: {issue.Message}</List.Item>
            ))}
          </List>
          <Text size="sm" mt="sm">{summary.ErrorExplanation || 'These records would be rejected by the import, so they are left out of the queue.'}</Text>
          <Text size="sm" fw={500} mt="sm">Do you want to give further instructions so we can try to fix these errors?</Text>
          {fixing && (
            <Textarea
              mt="xs"
              placeholder='For example: "The code is the text before the colon" or "Skip rows with no phone number"'
              value={instruction}
              onChange={e => setInstruction(e.currentTarget.value)}
              autosize
              minRows={2}
              disabled={busy}
            />
          )}
          <Group mt="sm">
            {!fixing && <Button size="xs" variant="white" color="red" onClick={() => setFixing(true)}>Yes, I'll add instructions</Button>}
            {fixing && onRetry && (
              <Button size="xs" color="red" leftSection={<IconRefresh size={14} />} onClick={() => onRetry(instruction)} disabled={busy || !instruction.trim()}>Try again</Button>
            )}
          </Group>
          {queueableCount(summary) > 0 && (
            <Checkbox
              mt="sm"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.currentTarget.checked)}
              label={`I understand that ${heldBackCount(summary).toLocaleString()} records have errors and will not be imported. Import the rest anyway.`}
            />
          )}
          {queueableCount(summary) === 0 && (
            <Text size="sm" mt="sm">None of the records can be imported until these errors are fixed.</Text>
          )}
        </Alert>
      )}

      {summary && (
        <Group gap="xl">
          <Stat label="records to import" value={queueableCount(summary)} />
          {heldBackCount(summary) > 0 && <Stat label="will not be imported" value={heldBackCount(summary)} tone="red" />}
          <Stat label="records with warnings" value={softIssueCount(summary)} tone={softIssueCount(summary) ? 'yellow' : 'green'} />
          <Stat label="empty rows skipped" value={summary.SkippedEmptyRows} />
          {summary.SkippedByFilter > 0 && <Stat label="rows excluded by filter" value={summary.SkippedByFilter} />}
        </Group>
      )}

      {summary && softIssues(summary).length > 0 && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Warnings. These records are still imported." data-testid="soft-warnings">
          {softIssues(summary).slice(0, 8).map((i, idx) => (
            <Text size="sm" key={idx}>{i.Count.toLocaleString()} × {i.Target}: {i.Message}</Text>
          ))}
        </Alert>
      )}

      <Group justify="space-between">
        <SegmentedControl
          size="xs"
          value={issuesOnly ? 'issues' : 'all'}
          onChange={value => { setIssuesOnly(value === 'issues'); setPageIndex(0); }}
          data={[{ label: 'All records', value: 'all' }, { label: 'With issues', value: 'issues' }]}
        />
        <Button
          variant="light"
          size="xs"
          leftSection={<IconDownload size={14} />}
          loading={downloading}
          onClick={() => { setDownloading(true); ImportDraftService.downloadReview(draft.ID, () => setDownloading(false)); }}
        >
          Download with issues (Excel)
        </Button>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      <ScrollArea type="auto" offsetScrollbars w="100%" style={{ maxWidth: '100%' }}>
        <Table verticalSpacing={4} fz="xs" withTableBorder striped highlightOnHover miw={columns.length * 140}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Row</Table.Th>
              <Table.Th>Status</Table.Th>
              {extracted && <Table.Th>Source</Table.Th>}
              {columns.map(c => <Table.Th key={c}>{c}</Table.Th>)}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody key={requestKey}>
            {loading && (
              <Table.Tr><Table.Td colSpan={columns.length + 2}><Group justify="center" p="md"><Loader size="sm" /></Group></Table.Td></Table.Tr>
            )}
            {!loading && page?.Rows.map(row => {
              const held = row.Warnings.some(w => w.BlocksQueue);
              const heldTargets = new Set(row.Warnings.filter(w => w.BlocksQueue).map(w => w.Target));
              const warned = new Set(row.Warnings.map(w => w.Target));
              return (
                <Table.Tr key={`${pageIndex}-${row.SourceRowIndex}`}>
                  <Table.Td>{row.SourceRowIndex + 1}</Table.Td>
                  <Table.Td>
                    {row.Warnings.length === 0
                      ? <Badge size="xs" color="green" variant="light">OK</Badge>
                      : (
                        <Tooltip label={row.Warnings.map(w => `${w.Target}: ${w.Message}`).join('\n')} multiline w={320}>
                          <Badge size="xs" color={held ? 'red' : 'yellow'} variant="light">
                            {held ? 'Held back' : `${row.Warnings.length} issue${row.Warnings.length === 1 ? '' : 's'}`}
                          </Badge>
                        </Tooltip>
                      )}
                  </Table.Td>
                  {extracted && (
                    <Table.Td style={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.SourceText || ''} c="dimmed">
                      {row.SourceText || ''}
                    </Table.Td>
                  )}
                  {columns.map(c => (
                    <Table.Td key={c} style={heldTargets.has(c) ? { textDecoration: 'underline dotted', textDecorationColor: 'var(--mantine-color-red-7)' } : warned.has(c) ? { textDecoration: 'underline dotted', textDecorationColor: 'var(--mantine-color-yellow-7)' } : undefined}>
                      {row.Values[c] ?? ''}
                    </Table.Td>
                  ))}
                </Table.Tr>
              );
            })}
            {!loading && page && page.Rows.length === 0 && (
              <Table.Tr><Table.Td colSpan={columns.length + 2}><Text size="sm" c="dimmed" ta="center" p="md">{issuesOnly ? 'No records have issues.' : 'No records.'}</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {onReread && (
        <Stack gap="xs">
          <Textarea
            label="Add instructions and process again"
            placeholder='For example: "The code is the text before the colon" or "Ignore the Sales Rep lines"'
            value={instruction}
            onChange={e => setInstruction(e.currentTarget.value)}
            autosize
            minRows={1}
            disabled={busy}
          />
          <Group>
            <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={() => onReread(instruction)} disabled={busy}>Process again</Button>
          </Group>
        </Stack>
      )}

      <Group justify="space-between">
        <Group gap="sm">
          <Pagination size="sm" total={pageCount} value={pageIndex + 1} onChange={p => setPageIndex(p - 1)} />
          {range && <Text size="xs" c="dimmed" data-testid="review-page-range">{range}</Text>}
        </Group>
        <Group>
          <Button variant="subtle" c="dimmed" onClick={onBack} data-testid="check-mapping">Check the mapping</Button>
          <Button onClick={onContinue} disabled={!summary || queueableCount(summary) === 0 || (heldBackCount(summary) > 0 && !acknowledged)}>Continue</Button>
        </Group>
      </Group>
    </Stack>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div>
      <Text fw={700} size="lg" c={tone}>{value.toLocaleString()}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </div>
  );
}
